import React, { useState, useRef, useEffect } from 'react';
import { FaUpload, FaTimes, FaPlus, FaSave, FaTrash, FaImage, FaArrowLeft } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import PortfolioList from './PortfolioList';
import api from "../../services/axiosInstance"

 
const AddPortfolio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fullDescription: '',
    gradient: 'from-blue-500 to-purple-600',
    projectType: 'residential',  
    date: '',
    location: '',
    surface: '',
    duration: ''
  });

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const fileInputRef = useRef(null);

  const gradientOptions = [
    { value: 'from-blue-500 to-purple-600', label: 'Blue to Purple' },
    { value: 'from-green-500 to-teal-600', label: 'Green to Teal' },
    { value: 'from-orange-500 to-red-600', label: 'Orange to Red' },
    { value: 'from-pink-500 to-rose-600', label: 'Pink to Rose' },
    { value: 'from-indigo-500 to-blue-600', label: 'Indigo to Blue' },
    { value: 'from-yellow-500 to-orange-600', label: 'Yellow to Orange' }
  ];

  const projectTypes = [
    { value: 'residential', label: 'Résidentiel' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industriel' },
    { value: 'institutional', label: 'Institutionnel' },
    { value: 'landscape', label: 'Paysage' }
  ];

  useEffect(() => {
    if (isEditMode) {
      fetchPortfolio();
    }
  }, [id]);

  const fetchPortfolio = async () => {
  try {
    const response = await api.get(`/portfolio/${id}`);
    const data = response.data;

    setFormData({
      title: data.title || "",
      description: data.description || "",
      fullDescription: data.full_description || "",
      gradient: data.gradient || "from-blue-500 to-purple-600",
      projectType: data.projectType || "residential",
      date: data.date || "",
      location: data.location || "",
      surface: data.surface || "",
      duration: data.duration || "",
    });

    setExistingImages(data.images || []);
  } catch (error) {
    setMessage("Erreur: " + (error.response?.data?.message || error.message));
  } finally {
    setLoading(false);
  }
};


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        setMessage('Erreur: Seules les images sont autorisées');
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage('Erreur: La taille des images ne doit pas dépasser 10MB');
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreviews(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });

    setImages(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setMessage("");

  try {
    const formDataToSend = new FormData();

    // Append form fields
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    // Append existing images
    formDataToSend.append("existingImages", JSON.stringify(existingImages));

    // Append new uploaded images
    images.forEach((image) => {
      formDataToSend.append("images", image);
    });

    const endpoint = isEditMode ? `/portfolio/${id}` : "/portfolio";

    const response = isEditMode
      ? await api.put(endpoint, formDataToSend)
      : await api.post(endpoint, formDataToSend);

    setMessage(
      isEditMode
        ? "Projet modifié avec succès!"
        : "Projet ajouté avec succès!"
    );

    // Reset form only if adding a new project
    if (!isEditMode) {
      setFormData({
        title: "",
        description: "",
        fullDescription: "",
        gradient: "from-blue-500 to-purple-600",
        projectType: "residential",
        date: "",
        location: "",
        surface: "",
        duration: "",
      });

      setImages([]);
      setImagePreviews([]);
      setExistingImages([]);
    }

    // Redirect after 2 seconds
    setTimeout(() => {
      navigate("/edit-portfolio");
    }, 2000);
  } catch (error) {
    setMessage(
      "Erreur: " + (error.response?.data?.message || error.message)
    );
  } finally {
    setIsSubmitting(false);
  }
};


  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleBack = () => {
    navigate('/edit-portfolio');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaArrowLeft />
                Retour
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {isEditMode ? 'Modifier le Projet' : 'Ajouter un Projet'}
                </h1>
                <p className="text-gray-600">
                  {isEditMode ? 'Modifiez les détails de votre projet' : 'Remplissez les détails de votre nouveau projet'}
                </p>
              </div>
            </div>
          </div>

          {message && (
            <div className={`p-4 rounded-lg mb-6 ${
              message.includes('Erreur') 
                ? 'bg-red-100 text-red-700 border border-red-300' 
                : 'bg-green-100 text-green-700 border border-green-300'
            }`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rest of your form remains the same */}
            {/* Basic Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du projet *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Résidence Moderne"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de projet *
                </label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {projectTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Descriptions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description courte *
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brève description du projet"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description complète *
              </label>
              <textarea
                name="fullDescription"
                value={formData.fullDescription}
                onChange={handleInputChange}
                required
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Description détaillée du projet..."
              />
            </div>

            {/* Project Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="text"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: 2024"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Localisation *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Alger"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Surface *
                </label>
                <input
                  type="text"
                  name="surface"
                  value={formData.surface}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: 450 m²"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée *
                </label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: 18 mois"
                />
              </div>
            </div>



            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images du projet
              </label>
              
              {/* Upload Area */}
              <div 
                onClick={triggerFileInput}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors mb-4"
              >
                <FaImage className="text-gray-400 text-3xl mx-auto mb-3" />
                <p className="text-gray-600 mb-2">Cliquez pour sélectionner des images</p>
                <p className="text-sm text-gray-500">PNG, JPG, JPEG jusqu'à 10MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Images existantes:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {existingImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={`${process.env.REACT_APP_API_URL}${image}`}
                          alt={`Existing ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Nouvelles images:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <FaTimes size={12} />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                          {images[index]?.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Files Info */}
              {(images.length > 0 || existingImages.length > 0) && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    {existingImages.length} image(s) existante(s) + {images.length} nouvelle(s) image(s)
                  </p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaSave />
                {isSubmitting 
                  ? (isEditMode ? 'Modification en cours...' : 'Ajout en cours...')
                  : (isEditMode ? 'Modifier le Projet' : 'Ajouter le Projet')
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPortfolio;